import { useEffect, useState } from 'react';
import { useActionData, useNavigate } from 'react-router';
import { useIntl } from 'react-intl';
import Page from 'client/components/aria/Page/Page';
import EnquiryPageTitle from 'client/pages/EnquiriesPage/components/EnquiryPageTitle/EnquiryPageTitle';
import EnquiryForm from './EnquiryForm/EnquiryForm';
import { HOME_PAGE, MAP_PAGE, VIEW_ENQUIRY_PAGE } from 'client/utils/routes/routeConstants';
import ErrorSummary from 'client/components/GDS/ErrorSummary/ErrorSummary';
import { Breadcrumbs } from 'client/components/GDS/Breadcrumbs/Breadcrumbs';
import BackLink from 'client/components/aria/BackLink/BackLink';
import { permissions } from 'client/utils/constants/permissions';
import { useHasUserPermission } from 'client/utils/auth/userPermissions';
import EnquiryTypeSelectionForm from './EnquiryTypeSelectionForm/EnquiryTypeSelectionForm';

const CreateEnquiryPage = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { enquiry, error, createdEnquiryId } = useActionData() || {};
    const [showSummary, setShowSummary] = useState(false);
    const [errors, setErrors] = useState([]);
    const hasEnquiryCreatePermission = useHasUserPermission(permissions.ENQUIRY_CREATE);
    const hasEmergencyPermission = useHasUserPermission(permissions.EMERGENCY_I2D_CREATE);
    const hasI2dPermission = useHasUserPermission(permissions.I2D_CREATE);
    const hasEmergencyAndI2dCreatePermission = hasEmergencyPermission && hasI2dPermission;
    const [draftEnquiry, setDraftEnquiry] = useState(null);

    useEffect(() => {
        if (createdEnquiryId) {
            // Navigate to confirmation page with query param to trigger notification-statuses API call instead of affected-asset-owners
            navigate(VIEW_ENQUIRY_PAGE + '/' + createdEnquiryId + '?confirmation=true', { state: { justCreated: true } });
            return;
        }

        if (!draftEnquiry && !enquiry) {
            // Redirect to map if accessed directly without enquiry data
            navigate(MAP_PAGE);
        }
    }, [createdEnquiryId, draftEnquiry, enquiry, navigate]);

    const resolveInitialIsEmergency = () => {
        if (hasEmergencyAndI2dCreatePermission) {
            return null;
        }
        if (hasI2dPermission || hasEnquiryCreatePermission) {
            return false;
        }
        if (hasEmergencyPermission) {
            return true;
        }

        return null;
    };

    useEffect(() => {
        if (!enquiry) {
            setDraftEnquiry(null);
            return;
        }

        const initialIsEmergency = enquiry?.isEmergency ?? resolveInitialIsEmergency();
        setDraftEnquiry({
            ...enquiry,
            isEmergency: initialIsEmergency,
            utilityEnquiryType: enquiry?.utilityEnquiryType ?? null
        });
    }, [enquiry, hasEmergencyAndI2dCreatePermission, hasI2dPermission, hasEmergencyPermission]);

    const handleEnquiryTypeSelection = (utilityEnquiryType) => {
        const isEmergency = utilityEnquiryType === 'unplanned';
        const startDate = new Date();
        startDate.setHours(0, 0, 0, 0);

        setDraftEnquiry(prev => ({
            ...prev,
            utilityEnquiryType,
            isEmergency
        }));
    };

    const clearEnquiryTypeSelection = () => {
        setDraftEnquiry(prev => ({
            ...prev,
            utilityEnquiryType: null,
            isEmergency: null
        }));
        setShowSummary(false);
        setErrors([]);
    };

    const changeEnquiryType = () => {
        clearEnquiryTypeSelection();
    };

    const handleBackLink = () => {
        if (showSummary) {
            setShowSummary(false);
            setErrors([]);
        } else {
            // If user doesn't have both roles, they skipped step 1, so go back to map
            if (!hasEmergencyAndI2dCreatePermission) {
                navigate(MAP_PAGE);
                return;
            }
            clearEnquiryTypeSelection();
        }

        const navigationElement = document.getElementById('navigation');
        if (navigationElement) {
            navigationElement.scrollIntoView();
        }
    };

    const isFirstStep = draftEnquiry?.utilityEnquiryType === null;
    const titleMessage = showSummary
        ? intl.formatMessage({ id: 'createEnquiryPage.title.summary' })
        : (isFirstStep
            ? intl.formatMessage({ id: 'createEnquiryPage.select.enquiry.type.title' })
            : intl.formatMessage({ id: 'createEnquiryPage.title' }));

    const breadcrumbsData = [
        { name: intl.formatMessage({ id: 'navbar.link.home' }), link: HOME_PAGE },
        { name: intl.formatMessage({ id: 'navbar.link.map' }), link: MAP_PAGE },
        ...(!isFirstStep
            ? [{ name: intl.formatMessage({ id: 'createEnquiryPage.select.enquiry.type.title' }), onClick: clearEnquiryTypeSelection }]
            : [{ name: intl.formatMessage({ id: 'createEnquiryPage.select.enquiry.type.title' }), ariaCurrent: 'page' }]),
        ...(!isFirstStep
            ? [{ name: intl.formatMessage({ id: 'createEnquiryPage.title' }), ariaCurrent: 'page' }]
            : [])
    ];

    return draftEnquiry && (
        <Page
            title={intl.formatMessage({ id: 'createEnquiryPage.page.title' })}
            data-testid="createEnquiryPage">
            <div className="govuk-grid-row govuk-!-margin-top-4">
                <div className="govuk-grid-column-full">
                    {errors.length > 0 && <ErrorSummary errors={errors} />}
                    {isFirstStep
                        ? <Breadcrumbs items={breadcrumbsData} />
                        : <BackLink onClick={handleBackLink} data-testid="createEnquiryPage.backLink">
                            {intl.formatMessage({ id: 'general.back' })}
                        </BackLink>
                    }
                    <EnquiryPageTitle
                        error={error}
                        isEmergency={!showSummary && draftEnquiry?.isEmergency}
                        showWarning={false}
                        id="createEnquiryPageTitle"
                        subtitleMessageId="createEnquiryPage.subtitle"
                    >
                        {titleMessage}
                    </EnquiryPageTitle>
                </div>
            </div>
            <>
                <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible" />
                {isFirstStep
                    ? <div data-testid="step-1-enquiry-type-selection">
                        <EnquiryTypeSelectionForm
                            setShowSummary={setShowSummary}
                            onSelectEnquiryType={handleEnquiryTypeSelection}
                            selectedEnquiryType={draftEnquiry?.utilityEnquiryType}
                            canSelectPlanned={hasI2dPermission || hasEnquiryCreatePermission}
                            canSelectUnplanned={hasEmergencyPermission}
                        />
                    </div>
                    : <div className="govuk-grid-row" data-testid={showSummary ? 'step-3-summary' : 'step-2-enquiry-details-form'}>
                        <div className="govuk-grid-column-full">
                            <EnquiryForm
                                enquiry={draftEnquiry}
                                onChange={setDraftEnquiry}
                                showSummary={showSummary}
                                setShowSummary={setShowSummary}
                                setErrors={setErrors}
                                changeEnquiryType={changeEnquiryType}
                                canChangeEnquiryType={(hasI2dPermission || hasEnquiryCreatePermission) && hasEmergencyPermission}
                                hasBothRoles={hasEmergencyAndI2dCreatePermission}
                            />
                        </div>
                    </div>
                }
            </>
        </Page>
    );
};

export default CreateEnquiryPage;
